/**
 * Date and route helpers for affiliate-based multi-city packages.
 *
 * A segment is a distinct one-way search, NEVER a round-trip to its starting
 * point when later itinerary stops exist. Availability/pricing is not implied.
 */
const ISO = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 86400000;

export function parseISODate(value) {
  if (typeof value !== 'string' || !ISO.test(value)) return null;
  const d = new Date(value + 'T00:00:00Z');
  return Number.isFinite(d.getTime()) && d.toISOString().slice(0, 10) === value ? d : null;
}

export function formatUTCDate(date) {
  return date.toISOString().slice(0, 10);
}

export function localTodayISO(now = new Date()) {
  // A Bulgarian traveler just after local midnight may still be on the
  // previous UTC date. The date picker must follow the user's calendar.
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  return formatUTCDate(d);
}

export function defaultPackageStart(now = new Date()) {
  const day = parseISODate(localTodayISO(now));
  return new Date(day.getTime() + 14 * MS_PER_DAY);
}

export function validFutureDeparture(value, now = new Date()) {
  const d = parseISODate(value);
  const today = parseISODate(localTodayISO(now));
  return d && d >= today ? d : null;
}

export function computeSegmentDates(segments, baseDate) {
  const date = baseDate instanceof Date && Number.isFinite(baseDate.getTime())
    ? baseDate : parseISODate(baseDate);
  if (!date) return [];
  if (!Array.isArray(segments) || segments.length === 0) return [];
  let cursor = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  const out = [];
  for (const segment of segments) {
    // Skipping a broken middle leg would deceptively imply a continuous
    // bookable route. Reject the whole itinerary until it is repaired.
    if (!segment || !/^[A-Z]{3}$/.test(String(segment.fromCode || ''))
        || !/^[A-Z]{3}$/.test(String(segment.toCode || ''))
        || segment.fromCode === segment.toCode
        || !segment.fromCity || !segment.toCity
        || (out.length > 0 && out[out.length - 1].toCode !== segment.fromCode)) return [];
    const nights = Number(segment.nights);
    if (!Number.isInteger(nights) || nights < 0 || nights > 45) return [];
    const startDate = formatUTCDate(new Date(cursor));
    cursor += nights * MS_PER_DAY;
    out.push({ ...segment, nights, startDate, endDate: formatUTCDate(new Date(cursor)) });
  }
  return out;
}

/**
 * Insert genuinely searchable optional destinations into an existing route.
 * The final trip-home flight is rebuilt from the LAST selected city.
 * Duplicate/unsupported options do not create circular or fake legs.
 */
export function extendPackageSegments(baseSegments, extensions = [], selectedCodes = []) {
  const base = Array.isArray(baseSegments) ? [...baseSegments] : [];
  if (!base.length) return [];
  const origin = base[0];
  const codeRE = /^[A-Z]{3}$/;
  if (!codeRE.test(String(origin.fromCode || ''))) return base;
  const last = base[base.length - 1];
  const hasReturn = last.nights === 0 && last.toCode === origin.fromCode && base.length > 1;
  const outward = hasReturn ? base.slice(0, -1) : base;
  const existing = new Set([origin.fromCode, ...outward.map(seg => seg.toCode)]);
  const additional = [];
  let cursor = outward[outward.length - 1];
  for (const ext of extensions) {
    const code = ext?.cityKey;
    if (!selectedCodes.includes(code) || !codeRE.test(String(code || ''))
        || !ext.city || existing.has(code) || !cursor?.toCode) continue;
    const nights = Number.isInteger(ext.nights) && ext.nights >= 1 && ext.nights <= 14
      ? ext.nights : 2;
    additional.push({
      id: `addon-${code.toLowerCase()}`,
      type: 'flight+hotel',
      fromCode: cursor.toCode, toCode: code,
      fromCity: cursor.toCity, toCity: ext.city,
      nights, hotel: { searchOnly: true },
      isExtension: true,
    });
    existing.add(code);
    cursor = additional[additional.length - 1];
  }
  if (!additional.length) return base;
  return [...outward, ...additional, {
    ...(hasReturn ? last : {}),
    id: hasReturn ? last.id : `return-${origin.fromCode.toLowerCase()}`,
    type: 'flight-only',
    fromCode: cursor.toCode, fromCity: cursor.toCity,
    toCode: origin.fromCode, toCity: origin.fromCity,
    nights: 0, hotel: undefined,
  }];
}

/** Human-readable complete route, including all cities and the final way home. */
export function packageRouteLabel(pkg) {
  const segments = pkg?.segments || [];
  if (!segments.length) return Array.isArray(pkg?.destinations) ? pkg.destinations.join(' · ') : '';
  const cities = [];
  for (const seg of segments) {
    if (seg?.fromCity && cities[cities.length - 1] !== seg.fromCity) cities.push(seg.fromCity);
    if (seg?.toCity && cities[cities.length - 1] !== seg.toCity) cities.push(seg.toCity);
  }
  return cities.join(' → ');
}

export function segmentFlightSearch(segment, adults = 2, children = 0) {
  if (!segment || !segment.startDate || !segment.fromCode || !segment.toCode) return null;
  // Every leg, including the final journey home, is one-way. A second
  // segment supplies the next flight rather than hidden auto-return travel.
  const party = Math.min(9, Math.max(1, Number(adults) + Number(children) || 1));
  return {
    departure: segment.fromCode,
    arrival: segment.toCode,
    departure_at: segment.startDate,
    return_at: '',
    passengers: party,
    inventorySource: 'redirect_only',
  };
}

export function getPackageStartDate(pkgId, now = new Date()) {
  const fallback = defaultPackageStart(now);
  if (typeof window === 'undefined') return fallback;
  try {
    // Explicit date in a shared URL takes precedence over this browser's
    // old saved date for the same package.
    if (window.location.pathname === `/trip/${encodeURIComponent(pkgId)}`) {
      const date = new URLSearchParams(window.location.search).get('d');
      const shareDate = validFutureDeparture(date, now);
      if (shareDate) return shareDate;
    }
    const saved = JSON.parse(window.sessionStorage.getItem('travelai:pkg:departure-date') || 'null');
    if (saved?.id === pkgId) {
      return validFutureDeparture(saved.date, now) || fallback;
    }
  } catch { /* disabled session storage or bad params */ }
  return fallback;
}
